import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { absoluteUrl } from "@/lib/uploads";
import { Sparkles, Crown, Share2, Image as ImageIcon, ShoppingBag, Wand2, ExternalLink, Heart } from "lucide-react";
import { toast } from "sonner";

export default function ProfilePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [subs, setSubs] = useState([]);
  const [portalBusy, setPortalBusy] = useState(false);

  useEffect(() => {
    api.get("/profile/me").then((r) => setData(r.data));
    api.get("/subscriptions/mine").then((r) => setSubs(r.data)).catch(() => {});
  }, []);

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const r = await api.post("/customer-portal/session", { return_url: window.location.origin + "/profile" });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Portal unavailable");
      setPortalBusy(false);
    }
  };

  const share = () => {
    const url = `${window.location.origin}/u/${user.user_id}`;
    navigator.clipboard?.writeText(url);
    toast.success("Public link copied");
  };

  if (!data) return <div className="text-[#64748B]">Loading empire…</div>;

  return (
    <div className="space-y-6">
      <div className="glass-heavy rounded-3xl p-6 grain flex flex-wrap items-center gap-6">
        <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-[#00E5FF] to-[#7B2CBF] flex items-center justify-center font-bold text-3xl text-white glow-ring">
          {user?.name?.[0]?.toUpperCase()}
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="text-xs uppercase tracking-[0.25em] text-[#F5C542] flex items-center gap-2"><Crown size={12} /> digital empire</div>
          <h1 className="font-display text-3xl font-bold mt-1">{user?.name}</h1>
          <div className="text-sm text-[#64748B]">{user?.email}</div>
        </div>
        <div className="flex gap-2">
          <button data-testid="share-profile" onClick={share} className="nexus-btn-secondary text-sm flex items-center gap-2"><Share2 size={14} /> Share</button>
          <button data-testid="view-public" onClick={() => navigate(`/u/${user.user_id}`)} className="nexus-btn-primary text-sm">View public</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { l: "Posts", v: data.stats.posts, c: "#00E5FF" },
          { l: "Products", v: data.stats.products, c: "#F5C542" },
          { l: "Generations", v: data.stats.generations, c: "#7B2CBF" },
          { l: "Reactions", v: data.stats.reactions, c: "#0F172A" },
        ].map((s) => (
          <div key={s.l} className="glass rounded-2xl p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#64748B]">{s.l}</div>
            <div className="font-display text-3xl font-bold mt-1" style={{ color: s.c }}>{s.v}</div>
          </div>
        ))}
      </div>

      {/* Posts */}
      <Section icon={Sparkles} title="Your signals" testid="profile-posts">
        {data.posts.length === 0 ? (
          <Empty text="No signals yet — visit Social." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.posts.map((p) => (
              <div key={p.id} className="glass rounded-2xl p-4">
                {p.image_url && <img src={absoluteUrl(p.image_url)} alt="" className="rounded-xl w-full h-40 object-cover mb-2" />}
                {p.video_url && (
                  <video src={absoluteUrl(p.video_url)} controls className="rounded-xl w-full h-40 object-cover mb-2" />
                )}
                <p className="text-sm text-[#0F172A] line-clamp-3">{p.content}</p>
                <div className="text-xs text-[#94A3B8] mt-2">{new Date(p.created_at).toLocaleDateString()} · {p.zone}</div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section icon={ShoppingBag} title="Your storefront" testid="profile-products">
        {data.products.length === 0 ? (
          <Empty text="No listings — sell a digital good in Marketplace." />
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {data.products.map((p) => (
              <div key={p.id} className="glass rounded-2xl overflow-hidden">
                {p.image_url && <img src={p.image_url} alt={p.title} className="w-full h-32 object-cover" />}
                <div className="p-3">
                  <div className="font-display font-semibold text-sm line-clamp-1">{p.title}</div>
                  <div className="text-[#00E5FF] font-bold mt-1">${p.price.toFixed(2)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section icon={Wand2} title="Your generations" testid="profile-generations">
        {data.generations.length === 0 ? (
          <Empty text="No manifests yet — try the AI Studio." />
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {data.generations.map((g) => (
              <div key={g.id} className="glass rounded-2xl overflow-hidden">
                <img src={g.image_b64} alt={g.prompt} className="w-full h-32 object-cover" />
                <div className="p-2 text-xs text-[#64748B] line-clamp-2">{g.prompt}</div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section icon={Heart} title="Your patronages" testid="profile-subscriptions">
        {subs.length === 0 ? (
          <Empty text="You don't subscribe to anyone yet. Find a creator on /u/<id> and tap Subscribe." />
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {subs.map((s) => (
                <div key={s.id} className="glass rounded-2xl p-4 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#F5C542]/30 to-[#7B2CBF]/30 flex items-center justify-center">
                    <Crown size={18} className="text-[#F5C542]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-sm capitalize">{s.tier} patron</div>
                    <div className="text-xs text-[#64748B]">
                      ${s.amount?.toFixed(2)}/mo
                      {s.auto_renew && <span className="ml-2 text-[#00E5FF]">· auto-renew</span>}
                      {s.cancel_at_period_end && <span className="ml-2 text-[#FF6B9D]">· canceling</span>}
                    </div>
                    <div className="text-[10px] text-[#94A3B8] mt-1">until {new Date(s.expires_at).toLocaleDateString()}</div>
                  </div>
                  <button onClick={() => navigate(`/u/${s.creator_id}`)} className="text-xs text-[#00E5FF] hover:underline">View</button>
                </div>
              ))}
            </div>
            <div className="mt-4">
              <button
                data-testid="open-portal"
                onClick={openPortal}
                disabled={portalBusy}
                className="nexus-btn-primary text-sm flex items-center gap-2"
              >
                <ExternalLink size={14} /> {portalBusy ? "Opening…" : "Manage subscriptions in Stripe"}
              </button>
              <p className="text-xs text-[#94A3B8] mt-2">Cancel · update card · download invoices — all in Stripe's secure portal.</p>
            </div>
          </>
        )}
      </Section>
    </div>
  );
}

function Section({ icon: Icon, title, children, testid }) {
  return (
    <div className="glass-heavy rounded-3xl p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <Icon size={18} className="text-[#00E5FF]" />
        <div className="font-display font-bold">{title}</div>
      </div>
      {children}
    </div>
  );
}

function Empty({ text }) {
  return <div className="text-sm text-[#64748B]">{text}</div>;
}
